import { useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ApiError, passkeyLogin, supportsPasskeys } from '../lib/api'
import { WebAuthnUserError } from '../lib/webauthnCeremony'
import { EmailCodePanel } from '../components/EmailCodePanel'

export function PasskeyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setError(null)
    if (!supportsPasskeys()) {
      setError(t('auth.webauthnUnsupported'))
      inFlight.current = false
      return
    }
    setBusy(true)
    try {
      await passkeyLogin(email.trim() || undefined)
      navigate('/inbox', { replace: true })
    } catch (err) {
      if (err instanceof WebAuthnUserError) {
        if (err.code === 'cancelled') setError(t('auth.passkeyCancelled'))
        else if (err.code === 'pending') setError(t('auth.passkeyPending'))
        else setError(err.message)
      } else if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError(t('auth.passkeyFailed'))
      }
    } finally {
      setBusy(false)
      inFlight.current = false
    }
  }

  return (
    <section className="welcome card">
      <h1>{t('auth.passkeyTitle')}</h1>
      <p className="lede">{t('auth.passkeyLede')}</p>
      <form className="auth-form" onSubmit={onSubmit}>
        <label className="field">
          <span>{t('auth.emailOptional')}</span>
          <input
            type="email"
            autoComplete="username webauthn"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
        {error ? (
          <p className="warn" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? t('auth.working') : t('welcome.continuePasskey')}
        </button>
      </form>
      <EmailCodePanel mode="login" />
      <p className="muted">
        <Link to="/recovery">{t('auth.useRecovery')}</Link>
        {' · '}
        <Link to="/welcome">{t('auth.backWelcome')}</Link>
      </p>
    </section>
  )
}
